/**
 * Affirmations.
 *
 * Built against taskdojo-app/src/screens/AffirmationsScreen.js. Documents live
 * in the global `affirmations` collection keyed by `userId`, with the phone's
 * shape: { text, userId, createdAt, updatedAt }. They use addDoc, so the
 * document carries no id of its own.
 *
 * The screen is two halves, as on the phone: today's line, large, with a way to
 * cycle through the others; and the list you can add to and edit.
 *
 * Today's line comes from constants/affirmations, which ports the phone's
 * rotation exactly -- whole days since the epoch, modulo the count. Matching it
 * is the point: opening the app on your phone and then here on the same day
 * should show the same line. Cycling is a local browse, deliberately not
 * persisted, so it does not change what tomorrow shows.
 *
 * With nothing saved, the built-in list is shown rather than an empty card. The
 * phone does the same, and a screen whose entire purpose is to say something
 * encouraging should not open by saying nothing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, SummaryBand, ActionButton, PrimaryButton, SecondaryButton,
  Field, useInputClass, raised, pressable,
} from '../dojo';
import { DEFAULT_AFFIRMATIONS, dailyIndex } from '../../constants/affirmations';

interface Affirmation {
  id: string;
  text: string;
  userId: string;
  createdAt: any;
  updatedAt: any;
}

const Affirmations: React.FC = () => {
  const { state } = useApp();
  const { colors, isDark } = useTheme();
  const input = useInputClass();

  const uid = state.user?.uid;

  const [items, setItems] = useState<Affirmation[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<number | null>(null);
  const [editing, setEditing] = useState<Affirmation | null>(null);
  const [creating, setCreating] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Affirmation | null>(null);

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'affirmations'), where('userId', '==', uid));
    const unsub = onSnapshot(q, (snap) => {
      const out: Affirmation[] = [];
      snap.forEach((d) => out.push({ ...(d.data() as Affirmation), id: d.id }));
      out.sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
      setItems(out);
      setLoading(false);
    }, (e) => {
      setError(e?.message || "Couldn't load your affirmations.");
      setLoading(false);
    });
    return unsub;
  }, [uid]);

  /** Yours if you have any, otherwise the built-in list. */
  const pool = useMemo(
    () => (items.length > 0 ? items.map((i) => i.text) : DEFAULT_AFFIRMATIONS),
    [items],
  );

  const todayIndex = useMemo(() => dailyIndex(pool.length), [pool.length]);
  const shown = pool[(cursor ?? todayIndex) % pool.length] || '';
  const browsing = cursor !== null && cursor % pool.length !== todayIndex;

  const openCreate = () => { setText(''); setEditing(null); setCreating(true); setError(''); };
  const openEdit = (a: Affirmation) => { setText(a.text); setEditing(a); setCreating(false); setError(''); };
  const closeModal = () => { setEditing(null); setCreating(false); setError(''); };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uid) return;
    if (!text.trim()) { setError('Write something first.'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await updateDoc(doc(db, 'affirmations', editing.id), {
          text: text.trim(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'affirmations'), {
          text: text.trim(),
          userId: uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: Affirmation) => {
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'affirmations', a.id));
      setConfirmDelete(null);
      closeModal();
      setCursor(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that.");
    } finally {
      setSaving(false);
    }
  };

  const modalOpen = creating || !!editing;

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Affirmations"
          action={
            <ActionButton label="New affirmation" onClick={openCreate}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: items.length, label: 'Yours' },
            { value: pool.length, label: 'In rotation', color: isDark ? '#FEC200' : '#C08A1E' },
          ]}
        />

        {error && !modalOpen && (
          <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="px-5 space-y-6">
          {/* -------------------------- today's line -------------------------- */}
          <div className={`rounded-[18px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-6`}>
            <div className="flex items-center justify-between mb-4">
              <span className={`text-[11px] font-bold uppercase tracking-[0.6px] ${colors.textMuted}`}>
                {browsing ? 'From your rotation' : 'Today'}
              </span>
              <div className="flex gap-2">
                {browsing && (
                  <button
                    type="button"
                    onClick={() => setCursor(null)}
                    className={`h-9 px-3 rounded-[12px] ${raised} ${pressable} ${colors.cardBorder}
                      ${colors.card} ${colors.textSecondary} text-[12.5px] font-extrabold`}
                  >
                    Back to today
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCursor(((cursor ?? todayIndex) + 1) % pool.length)}
                  aria-label="Show another"
                  disabled={pool.length < 2}
                  className={`w-9 h-9 rounded-[12px] flex items-center justify-center ${raised} ${pressable}
                    ${colors.cardBorder} ${colors.card} ${colors.textSecondary} disabled:opacity-40`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                    <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20.5 9a9 9 0 0 0-14.9-3.4L1 10m22 4l-4.6 4.4A9 9 0 0 1 3.5 15"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            <p className="text-[22px] sm:text-[26px] font-black tracking-[-0.6px] leading-snug text-balance">
              {shown}
            </p>

            {items.length === 0 && !loading && (
              <p className={`text-[13px] font-semibold mt-4 ${colors.textSecondary}`}>
                These are the built-in ones. Add your own and they take over the rotation.
              </p>
            )}
          </div>

          {/* ---------------------------- your list ---------------------------- */}
          <div>
            <h2 className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-3 ${colors.textMuted}`}>
              Yours
            </h2>

            {loading ? (
              <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center text-center py-12">
                <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                  flex items-center justify-center mb-[18px] text-[34px]`}>✨</div>
                <h3 className="text-lg font-black tracking-[-0.3px] mb-1.5">Nothing of your own yet</h3>
                <p className={`text-[14.5px] font-semibold mb-5 ${colors.textSecondary}`}>
                  Write the thing you'd want to read on a bad day.
                </p>
                <button
                  type="button"
                  onClick={openCreate}
                  className={`rounded-[14px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
                    px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold`}
                >
                  Write one
                </button>
              </div>
            ) : (
              <ul className="space-y-2 list-none p-0 m-0">
                {items.map((a) => (
                  <li key={a.id}>
                    <div className={`rounded-[14px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                      px-4 py-3 flex items-center gap-3`}>
                      <p className="flex-1 min-w-0 text-[15px] font-semibold break-words">{a.text}</p>
                      <button
                        type="button"
                        onClick={() => openEdit(a)}
                        aria-label={`Edit “${a.text}”`}
                        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
                          ${colors.textMuted} ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'}`}
                      >
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z"
                            strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={closeModal}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-md ${colors.card} border-2 ${colors.cardBorder}
              rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <h2 className="text-[24px] font-black tracking-[-0.6px] mb-5">
              {editing ? 'Edit affirmation' : 'New affirmation'}
            </h2>

            <Field label="Affirmation" className="mb-5">
              <textarea
                className={`${input} min-h-[110px] resize-y leading-relaxed`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Small progress is still progress"
                maxLength={200}
                autoFocus
              />
            </Field>

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <PrimaryButton type="submit" disabled={saving} className="flex-1">
                {saving ? 'Saving…' : editing ? 'Save' : 'Add'}
              </PrimaryButton>
              {editing ? (
                <SecondaryButton
                  onClick={() => setConfirmDelete(editing)}
                  disabled={saving}
                  className="!text-dojo-red"
                >
                  Delete
                </SecondaryButton>
              ) : (
                <SecondaryButton onClick={closeModal} className="flex-1">Cancel</SecondaryButton>
              )}
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Delete this affirmation?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              “{confirmDelete.text}” will be removed from your rotation.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmDelete(null)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton onClick={() => remove(confirmDelete)} disabled={saving} className="flex-1">
                {saving ? 'Deleting…' : 'Delete'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Affirmations;
